import { Routes, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { inject } from '@angular/core';
import { AuthService } from '@services/auth.service';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, delay } from 'rxjs/operators';

// Guard para rutas protegidas
const authGuard = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return of(null).pipe(
    delay(0),
    map(() => {
      if (authService.isAuthenticated()) {
        return true;
      } else {
        router.navigate(['/login'], {
          queryParams: { returnUrl: state.url },
        });
        return false;
      }
    }),
  );
};

// Guard para redirigir usuarios autenticados
const guestGuard = (): Observable<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return of(null).pipe(
    delay(0),
    map(() => {
      if (!authService.isAuthenticated()) {
        return true;
      } else {
        router.navigate(['/loads']);
        return false;
      }
    }),
  );
};

// Guard para rutas que requieren rol de administrador
const adminGuard = (): Observable<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return of(null).pipe(
    delay(0),
    map(() => {
      if (authService.isAuthenticated()) {
        const userRole = authService.userRole();
        if (userRole?.toLowerCase() === 'admin' || userRole?.toLowerCase() === 'developer') {
          return true;
        } else {
          router.navigate(['/loads']);
          return false;
        }
      } else {
        router.navigate(['/login']);
        return false;
      }
    }),
  );
};

const developerGuard = (): Observable<boolean> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  return of(null).pipe(
    delay(0),
    map(() => {
      if (authService.isAuthenticated()) {
        const userRole = authService.userRole();
        if (userRole?.toLowerCase() === 'developer') {
          return true;
        } else {
          router.navigate(['/loads']);
          return false;
        }
      } else {
        router.navigate(['/login']);
        return false;
      }
    }),
  );
};

export const routes: Routes = [
  {
    path: '',
    redirectTo: '/loads',
    pathMatch: 'full',
  },
  {
    path: 'login',
    canActivate: [guestGuard],
    loadComponent: () => import('./pages/login/login.page').then((m) => m.LoginComponent),
  },
  {
    path: 'loads',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/load/load.page').then((m) => m.LoadComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/load/list-loads/list-load.page').then((m) => m.YuLoadComponent),
      },
      {
        path: 'create-load',
        loadComponent: () => import('./pages/load/create-load/create-load.page').then((m) => m.CreateLoadComponent),
      },
      {
        path: 'all',
        loadComponent: () => import('./pages/load/all-loads/all-loads.page').then((m) => m.AllLoadsPage),
      },
      {
        path: 'suggested-products',
        loadComponent: () =>
          import('./pages/load/list-suggested-products/list-suggested-products.page').then(
            (m) => m.ListSuggestedProductsPage,
          ),
      },
      {
        path: 'suggested-families',
        loadComponent: () =>
          import('./pages/load/list-suggested-families/list-suggested-families.page').then(
            (m) => m.ListSuggestedFamiliesPage,
          ),
      },
      {
        path: 'generic-suppliers',
        loadComponent: () =>
          import('./pages/load/list-generic-suppliers/list-generic-suppliers.page').then(
            (m) => m.ListGenericSuppliersPage,
          ),
      },
      {
        path: 'pending-generics',
        loadComponent: () =>
          import('./pages/load/list-pending-generics/list-pending-generics.page').then(
            (m) => m.ListPendingGenericsPage,
          ),
      },
    ],
  },
  {
    path: 'loads/:id',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/load/load-detail/load-detail.page').then((m) => m.LoadDetailComponent),
    children: [
      {
        path: '',
        redirectTo: 'documents',
        pathMatch: 'full',
      },
      {
        path: 'documents',
        loadComponent: () =>
          import('./pages/load/load-detail/documents/documents.page').then((m) => m.LoadDocumentsComponent),
      },
      {
        path: 'extracted_purchase_formats',
        loadComponent: () =>
          import('./pages/load/load-detail/extracted-purchase-formats/extracted-purchase-formats.page').then(
            (m) => m.LoadExtractedPurchaseFormatsComponent,
          ),
      },
      {
        path: 'general-expenses',
        loadComponent: () =>
          import('./pages/load/load-detail/list-general-expenses/list-general-expenses.page').then(
            (m) => m.ListGeneralExpensesComponent,
          ),
      },
      {
        path: 'suppliers',
        loadComponent: () =>
          import('./pages/load/load-detail/extracted-suppliers/extracted-suppliers.page').then(
            (m) => m.ExtractedSuppliersComponent,
          ),
      },
      {
        path: 'albaranes',
        loadComponent: () =>
          import('./pages/load/load-detail/albaranes/albaranes.page').then((m) => m.AlbaranesComponent),
      },
      {
        path: 'extracted-users',
        loadComponent: () =>
          import('./pages/load/load-detail/extracted-users/extracted-users.page').then(
            (m) => m.ExtractedUsersComponent,
          ),
      },
      {
        path: 'extracted-payrolls',
        loadComponent: () =>
          import('./pages/load/load-detail/extracted-payrolls/extracted-payrolls.page').then(
            (m) => m.ExtractedPayrollsComponent,
          ),
      },
      {
        path: 'reports',
        loadComponent: () => import('./pages/load/load-detail/report/report.page').then((m) => m.LoadReportsComponent),
      },
      {
        path: 'upload-history',
        loadComponent: () =>
          import('./pages/load/load-detail/upload-history/upload-history.page').then((m) => m.UploadHistoryComponent),
      },
      {
        path: 'upload-guide',
        loadComponent: () =>
          import('./pages/load/load-detail/upload-guide/upload-guide.page').then((m) => m.UploadGuidePage),
      },
      {
        path: 'audit-log',
        canActivate: [developerGuard],
        loadComponent: () =>
          import('./pages/load/load-detail/load-audit-log/load-audit-log.component').then(
            (m) => m.LoadAuditLogComponent,
          ),
      },
    ],
  },
  {
    path: 'analytics',
    canActivate: [developerGuard],
    loadComponent: () => import('./pages/analytics/analytics.page').then((m) => m.AnalyticsPage),
  },
  {
    path: 'users',
    canActivate: [adminGuard],
    loadComponent: () => import('./pages/users/users.page').then((m) => m.UsersPage),
  },
  {
    path: 'integration-api-keys',
    canActivate: [adminGuard],
    loadComponent: () =>
      import('./pages/integration-api-keys/integration-api-keys.page').then((m) => m.IntegrationApiKeysPage),
  },
  {
    path: 'keyword-blocks',
    canActivate: [authGuard],
    loadComponent: () => import('./pages/keyword-blocks/keyword-blocks.page').then((m) => m.KeywordBlocksPage),
  },
  {
    path: 'ai-guides',
    canActivate: [adminGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('./pages/ai-guides/ai-guides.page').then((m) => m.AIGuidesPage),
        children: [
          {
            path: '',
            loadComponent: () => import('./pages/ai-guides/ai-guides-list.page').then((m) => m.AIGuidesListPage),
          },
        ],
      },
      {
        path: 'create',
        loadComponent: () => import('./pages/ai-guides/ai-guide-form.page').then((m) => m.AIGuideFormPage),
      },
      {
        path: ':id',
        loadComponent: () => import('./pages/ai-guides/ai-guide-detail.page').then((m) => m.AIGuideDetailPage),
      },
      {
        path: ':id/edit',
        loadComponent: () => import('./pages/ai-guides/ai-guide-form.page').then((m) => m.AIGuideFormPage),
      },
    ],
  },
  {
    path: 'queues',
    canActivate: [developerGuard],
    loadComponent: () => import('./pages/queues/queues.page').then((m) => m.QueuesPage),
  },
  {
    path: 'feedback',
    canActivate: [developerGuard],
    loadComponent: () => import('./pages/feedback/feedback.page').then((m) => m.FeedbackPage),
  },
  {
    path: '**',
    loadComponent: () => import('./pages/not-found/not-found.page').then((m) => m.NotFoundComponent),
  },
];
